import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy, MapPin, Cake, Ruler, Hand, Target, Award, ChevronRight,
  ExternalLink, Loader2, Sparkles,
} from 'lucide-react';
import axios from 'axios';
import i18n from '../i18n';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const t = (k, o) => i18n.t(k, o);
const lang = () => (i18n.language || 'fr').slice(0, 2);
const loc = () => ({ fr: 'fr-FR', en: 'en-GB', es: 'es-ES', it: 'it-IT' }[lang()] || 'fr-FR');

const formatBirth = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(loc(), { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

const InfoTile = ({ id, icon: Icon, label, value, accent = false }) => (
  <div className={'rounded-xl p-3 ' + (accent ? 'bg-gradient-to-br from-[#32CD32]/15 to-[#28a428]/10 border border-[#32CD32]/30' : 'bg-[#F0F7FA]')} data-testid={`tile-${id}`}>
    <p className="text-[10px] uppercase tracking-wide text-[#5A8BA8] font-bold mb-1 flex items-center gap-1">
      <Icon className="w-3 h-3" /> {label}
    </p>
    <p className="text-sm font-bold text-[#033871] leading-snug">{value || '—'}</p>
  </div>
);

const PlayerDetailModal = ({ player, onClose }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // Derive slug from FIP url
  const slug = (() => {
    if (!player) return '';
    if (player.fip_url) {
      const m = player.fip_url.match(/\/player\/([^/]+)\/?/);
      if (m) return m[1];
    }
    return `${player.name || ''}-${player.surname || ''}`
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  })();

  useEffect(() => {
    if (!player || !slug) return;
    let alive = true;
    setLoading(true);
    setDetails(null);
    axios.get(`${API}/padpro/players/${slug}`, { params: { lang: lang() } })
      .then(res => { if (alive) setDetails(res.data.data || {}); })
      .catch(() => { if (alive) setDetails({}); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [player, slug]);

  // Lock body scroll while modal open + ESC to close
  useEffect(() => {
    if (!player) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handler);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handler);
    };
  }, [player, onClose]);

  if (!player) return null;

  const racketSlug = details?.brand ? details.brand.toLowerCase() : '';

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center px-3 py-4 sm:p-6" data-testid="player-modal">
      <div className="absolute inset-0 bg-[#021E47]/85 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col animate-[modalIn_0.25s_ease-out]">
        {/* HERO */}
        <div className={'relative h-44 sm:h-56 ' +
          (player.rank === 1 ? 'bg-gradient-to-br from-amber-400 via-amber-500 to-orange-500' :
            player.rank <= 3 ? 'bg-gradient-to-br from-[#0C63BF] via-[#033871] to-[#021E47]' :
              'bg-gradient-to-br from-[#033871] to-[#0C63BF]')}>
          {/* decorative blobs */}
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-black/10 blur-2xl" />

          {/* close */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 hover:bg-white shadow-md flex items-center justify-center text-[#033871] text-base font-bold z-10"
            data-testid="player-modal-close"
          >✕</button>

          <div className="relative flex items-end gap-4 h-full px-5 sm:px-7 pb-4">
            {/* Photo */}
            <div className="shrink-0 w-24 h-24 sm:w-32 sm:h-32 rounded-2xl bg-white/90 border-4 border-white shadow-2xl overflow-hidden flex items-center justify-center">
              {player.thumbnail ? (
                <img src={player.thumbnail} alt={player.full_name} className="w-full h-full object-contain p-1" onError={(e)=>{e.currentTarget.style.display='none';}} />
              ) : (
                <span className="text-3xl font-black text-[#033871]">{(player.surname || '')[0]}</span>
              )}
            </div>
            {/* Identity */}
            <div className="min-w-0 flex-1 text-white pb-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="bg-white text-[#033871] text-[10px] sm:text-xs font-black px-2 py-0.5 rounded-md shadow">N°{player.rank}</span>
                {player.country_flag && (
                  <img src={player.country_flag} alt={player.country_name} className="w-5 h-5 rounded-full object-cover border border-white/40" />
                )}
                <span className="text-[10px] sm:text-xs uppercase font-bold tracking-wide opacity-90">{player.country_name || player.nationality}</span>
              </div>
              <h2 className="text-xl sm:text-3xl font-black leading-tight drop-shadow-md">
                {player.name} <span className="font-black">{player.surname}</span>
              </h2>
              <p className="text-xs sm:text-sm text-white/80 mt-0.5">
                <span className="font-bold">{player.points?.toLocaleString(loc())}</span> {t('padpro.fip_points')} · {player.gender === 'female' ? t('padpro.circuit_f') : t('padpro.circuit_m')}
              </p>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="overflow-y-auto p-5 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-[#5A8BA8] text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('padpro.loading_player')}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 mb-5">
                <InfoTile id="Âge" icon={Cake} label={t('padpro.age')} value={details?.age ? t('padpro.years', { n: details.age }) : null} />
                <InfoTile id="Taille" icon={Ruler} label={t('padpro.height')} value={details?.height ? `${details.height} m` : null} />
                <InfoTile id="Main" icon={Hand} label={t('padpro.hand')} value={details?.hand && t(`padpro.val_${details.hand}`, details.hand)} />
                <InfoTile id="Position" icon={Target} label={t('padpro.position')} value={(details?.side || details?.playing_position) && t(`padpro.val_${details.side || details.playing_position}`, details.side || details.playing_position)} accent />
              </div>

              {details?.season && (
                <div className="rounded-2xl border border-[#E8F4FD] p-4 mb-5" data-testid="player-season">
                  <p className="text-[10px] uppercase tracking-wide text-[#0C63BF] font-bold mb-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> {t('padpro.live_title', { year: details.season.year })}
                  </p>
                  <p className="text-sm font-black text-[#033871]" data-testid="player-season-summary">
                    {details.season.rank && <>{t('padpro.world_rank', { rank: details.season.rank })} · </>}
                    {details.season.titles.length > 0 ? t('padpro.season_titles', { count: details.season.titles.length }) : t('padpro.no_season_titles')}
                  </p>
                  {details.season.titles.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {details.season.titles.map((title, i) => (
                        <span key={i} className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[11px] font-bold">{title}</span>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-[#94A3B8] mt-2">{t('padpro.live_updated')}</p>
                </div>
              )}

              {(details?.birthDate || details?.birthPlace) && (
                <div className="bg-[#F0F7FA] rounded-xl p-3 mb-5 flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-[#0C63BF] shrink-0" />
                  <p className="text-xs sm:text-sm text-[#3D5870]">
                    {details?.birthDate && (<><span className="font-bold text-[#033871]">{t('padpro.born', { date: formatBirth(details.birthDate) })}</span></>)}
                    {details?.birthPlace && (<> {t('padpro.born_in')} <span className="font-bold text-[#033871]">{details.birthPlace}</span></>)}
                  </p>
                </div>
              )}

              {/* Racket */}
              {details?.racket && (
                <div className="bg-gradient-to-br from-[#032957] to-[#0C63BF] rounded-2xl p-4 sm:p-5 mb-5 text-white" data-testid="player-racket">
                  <p className="text-[10px] uppercase tracking-wide text-[#32CD32] font-bold flex items-center gap-1 mb-2">
                    <Sparkles className="w-3 h-3" /> {t('padpro.his_racket')}
                  </p>
                  <p className="text-base sm:text-lg font-black leading-tight">{details.racket}</p>
                  {details.brand && (
                    <Link
                      to={`/catalogue?brand=${racketSlug}`}
                      className="inline-flex items-center gap-1 mt-3 px-3 py-1.5 bg-[#32CD32] hover:bg-[#28a428] text-white text-xs font-bold rounded-lg transition-colors"
                      onClick={onClose}
                    >
                      {t('padpro.see_brand', { brand: details.brand })} <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                  )}
                </div>
              )}

              {/* Palmarès */}
              {(details?.palmares && details.palmares.length > 0) && (
                <div className="mb-5" data-testid="player-palmares">
                  <p className="text-[11px] uppercase tracking-wide text-[#5A8BA8] font-bold mb-2 flex items-center gap-1">
                    <Trophy className="w-3.5 h-3.5 text-amber-500" /> {t('padpro.palmares')}
                  </p>
                  <ul className="space-y-1.5">
                    {details.palmares.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#3D5870]">
                        <Award className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!details?.racket && !details?.palmares && (
                <p className="text-center text-xs text-[#5A8BA8] py-3 italic">
                  {t('padpro.details_soon')}
                </p>
              )}

              {details?.fip_url && (
                <a
                  href={details.fip_url}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#0C63BF] hover:underline"
                >
                  {t('padpro.fip_profile')} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default PlayerDetailModal;
